/**
 * Centralized Error Handling and Loading States
 * 
 * Phase 3: Unified error handling and loading state management
 * across the entire application for consistency.
 */

import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/shared/LoadingSpinner';
import { AppError } from '@/types';

interface ErrorState {
  id: string;
  error: AppError;
  context?: string;
  timestamp: Date;
  retryable: boolean;
}

interface LoadingState {
  [key: string]: boolean;
}

interface AppStateContextType {
  // Error management
  errors: ErrorState[];
  addError: (error: AppError, context?: string, retryable?: boolean) => string;
  removeError: (id: string) => void;
  clearAllErrors: () => void;
  
  // Loading management  
  loadingStates: LoadingState;
  setLoading: (key: string, loading: boolean) => void;
  isLoading: (key: string) => boolean;
  isAnyLoading: () => boolean;
  
  // Network status
  isOnline: boolean;
  
  // Retry mechanism
  retryOperation: (errorId: string, operation: () => Promise<void>) => Promise<void>;
}

const AppStateContext = createContext<AppStateContextType | undefined>(undefined);

export const useAppState = () => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within AppStateProvider');
  }
  return context;
};

interface AppStateProviderProps {
  children: ReactNode;
}

export const AppStateProvider = ({ children }: AppStateProviderProps) => {
  const [errors, setErrors] = useState<ErrorState[]>([]);
  const [loadingStates, setLoadingStates] = useState<LoadingState>({});
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Error management
  const addError = useCallback((error: AppError, context?: string, retryable = true): string => {
    const id = `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const errorState: ErrorState = {
      id,
      error,
      context,
      timestamp: new Date(),
      retryable
    };
    
    setErrors(prev => [...prev, errorState]);
    
    // Auto-remove error after 10 seconds if not critical
    if (error.code !== 'CRITICAL_ERROR') {
      setTimeout(() => removeError(id), 10000);
    }
    
    return id;
  }, []);

  const removeError = useCallback((id: string) => {
    setErrors(prev => prev.filter(error => error.id !== id));
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors([]);
  }, []);

  // Loading management
  const setLoading = useCallback((key: string, loading: boolean) => {
    setLoadingStates(prev => ({
      ...prev,
      [key]: loading
    }));
  }, []);

  const isLoading = useCallback((key: string) => {
    return !!loadingStates[key];
  }, [loadingStates]);

  const isAnyLoading = useCallback(() => {
    return Object.values(loadingStates).some(Boolean);
  }, [loadingStates]);

  // Retry mechanism
  const retryOperation = useCallback(async (errorId: string, operation: () => Promise<void>) => {
    try {
      removeError(errorId);
      await operation();
    } catch (error) {
      // Re-add error if retry fails
      addError(
        error as AppError,
        'Retry failed',
        false // Mark as non-retryable after failed retry
      );
    }
  }, [addError, removeError]);

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const value: AppStateContextType = {
    errors,
    addError,
    removeError,
    clearAllErrors,
    loadingStates,
    setLoading,
    isLoading,
    isAnyLoading,
    isOnline,
    retryOperation
  };

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  );
};

// Error Display Component
export const ErrorDisplay = () => {
  const { errors, removeError, retryOperation, isOnline, clearAllErrors } = useAppState();

  if (errors.length === 0 && isOnline) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md">
      {/* Network Status */}
      {!isOnline && (
        <Alert variant="destructive">
          <WifiOff className="h-4 w-4" />
          <AlertDescription>
            You're offline. Some features may not work properly.
          </AlertDescription>
        </Alert>
      )}

      {/* Error Messages */}
      {errors.slice(-3).map((errorState) => (
        <Alert key={errorState.id} variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <div className="flex-1">
              <p className="font-medium">{errorState.error.message}</p>
              {errorState.context && (
                <p className="text-xs text-muted-foreground mt-1">
                  {errorState.context}
                </p>
              )}
            </div>
            <div className="flex gap-2 ml-2">
              {errorState.retryable && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    // This would need to be implemented with the original operation
                    console.log('Retry error:', errorState.id);
                  }}
                >
                  <RefreshCw className="h-3 w-3" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeError(errorState.id)}
              >
                ×
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ))}
      
      {/* Clear all button if multiple errors */}
      {errors.length > 1 && (
        <div className="text-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => clearAllErrors()}
            className="text-xs"
          >
            Clear all errors
          </Button>
        </div>
      )}
    </div>
  );
};

// Loading Overlay Component
export const LoadingOverlay = () => {
  const { isAnyLoading } = useAppState();

  if (!isAnyLoading()) return null;

  return (
    <div className="fixed inset-0 bg-background/50 backdrop-blur-sm z-40 flex items-center justify-center">
      <div className="bg-card p-6 rounded-lg shadow-lg">
        <LoadingSpinner size="lg" text="Loading..." />
      </div>
    </div>
  );
};

// Hook for managing operation states
export const useOperationState = (operationKey: string) => {
  const { setLoading, isLoading, addError } = useAppState();

  const executeOperation = useCallback(async <T,>(
    operation: () => Promise<T>,
    errorMessage = 'Operation failed'
  ): Promise<T | null> => {
    try {
      setLoading(operationKey, true);
      const result = await operation();
      return result;
    } catch (error) {
      addError(
        {
          code: 'OPERATION_ERROR',
          message: errorMessage,
          details: error
        },
        operationKey,
        true
      );
      return null;
    } finally {
      setLoading(operationKey, false);
    }
  }, [operationKey, setLoading, addError]);

  return {
    loading: isLoading(operationKey),
    executeOperation
  };
};